import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import type { ToolSummary } from '../../api/TraceabilityResponseType';
import { filterTools, findNearTool, findTool } from '../../utils/toolSummaries';
import { TextCombobox } from '@/components/form/inputs/TextCombobox';
import { Highlight } from '@/components/form/inputs/combobox/ComboboxPopup';
import { PickerStatus } from './PickerStatus';
import { countText } from './pickerHelpers';

/** Rows shown at most; the footer counts the others. */
export const TOOL_ID_PICKER_MAX_ROWS = 50;

export type ToolIdPickerProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** The company's tools; null (loading or unavailable) → plain text field, as before. */
  summaries: readonly ToolSummary[] | null;
  /** Enter on the field (the parent decides whether the ID can be used). */
  onEnter?: () => void;
  /** "Créer cet outil" on an unknown ID. */
  onCreate?: (toolId: string) => void;
  placeholder?: string;
  disabled?: boolean;
};

/** Tool ID field of the trace entry: pick one of the company's tools, or create the unknown one. */
export function ToolIdPicker({
  id,
  value,
  onChange,
  summaries,
  onEnter,
  onCreate,
  placeholder,
  disabled,
}: ToolIdPickerProps) {
  const { t, language } = useTranslation();
  const { aliasFor } = useReferenceAlias();
  const matches = useMemo(
    () => (summaries ? filterTools(value, summaries, aliasFor) : []),
    [summaries, value, aliasFor]
  );

  if (!summaries) {
    return (
      <Input
        id={id}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onEnter?.();
        }}
        placeholder={placeholder}
        disabled={disabled}
      />
    );
  }

  const statusId = `${id}-status`;
  const hidden = matches.length - TOOL_ID_PICKER_MAX_ROWS;
  const referenceLabel = (reference: string | null) => {
    if (!reference) return t('toolPickers.toolId.noReference');
    const alias = aliasFor(reference);
    return alias ? `${alias} (${reference})` : reference;
  };

  return (
    <>
      <TextCombobox<ToolSummary>
        id={id}
        value={value}
        onChange={onChange}
        options={matches.slice(0, TOOL_ID_PICKER_MAX_ROWS)}
        optionValue={(tool) => tool.toolId}
        optionKey={(tool) => tool.toolId}
        renderOption={(tool) => (
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate font-mono">
              <Highlight text={tool.toolId} query={value} />
            </span>
            <span className="truncate text-xs text-muted-foreground">
              <Highlight text={referenceLabel(tool.reference)} query={value} />
              {' · '}
              {countText(t, 'toolPickers.toolId.traces', tool.traceCount, true)}
              {tool.lastTraceAt &&
                ` · ${t('toolPickers.toolId.lastTrace', {
                  date: new Date(tool.lastTraceAt).toLocaleDateString(language),
                })}`}
            </span>
          </span>
        )}
        header={t('toolPickers.toolId.listTitle', { count: summaries.length })}
        footer={hidden > 0 ? t('toolPickers.toolId.more', { count: hidden }) : undefined}
        emptyLabel={t('toolPickers.toolId.empty')}
        onEnter={onEnter}
        placeholder={placeholder}
        disabled={disabled}
        inputClassName="font-mono"
        describedBy={statusId}
      />
      <ToolIdStatus
        id={statusId}
        value={value.trim()}
        summaries={summaries}
        onUse={onChange}
        onCreate={onCreate}
      />
    </>
  );
}

function ToolIdStatus({
  id,
  value,
  summaries,
  onUse,
  onCreate,
}: {
  id: string;
  value: string;
  summaries: readonly ToolSummary[];
  onUse: (toolId: string) => void;
  onCreate?: (toolId: string) => void;
}) {
  const { t } = useTranslation();
  const testId = 'tool-id-status';
  if (!value) return null;

  const tool = findTool(value, summaries);
  if (tool) {
    const traces = countText(t, 'toolPickers.toolId.traces', tool.traceCount, true);
    return (
      <PickerStatus kind="ok" id={id} testId={testId}>
        {tool.reference
          ? t('toolPickers.toolId.known', { reference: tool.reference, traces })
          : t('toolPickers.toolId.knownNoReference', { traces })}
      </PickerStatus>
    );
  }
  const near = findNearTool(value, summaries);
  if (near) {
    return (
      <PickerStatus kind="near" id={id} testId={testId}>
        {t('toolPickers.toolId.near', { value, toolId: near.toolId })}{' '}
        <Button type="button" variant="link" size="inline" onClick={() => onUse(near.toolId)}>
          {t('toolPickers.toolId.use')}
        </Button>
      </PickerStatus>
    );
  }
  return (
    <PickerStatus kind="near" id={id} testId={testId}>
      <p>{t('toolPickers.toolId.unknown', { value })}</p>
      {onCreate && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => onCreate(value)}
        >
          <Plus aria-hidden />
          {t('toolPickers.toolId.create')}
        </Button>
      )}
    </PickerStatus>
  );
}
